import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { parseMessage, getCatEmoji, getRandomTip } from "../utils/nlp";
import { showToast, notifyVente, notifyDepense } from "../utils/notifications";
import { getBizType, THEME, fm, today, now, dayName, uid, vibrate, playSound } from "../utils/helpers";

export default function Chat({ config, employee, transactions, onTransaction, onBack }) {
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState([{
    from: "bot", time: now(),
    text: `Salut ${employee?.name || config.ownerName} ! 👋\n\nTape tes transactions :\n\n💵 "vente 5000 riz"\n💸 "achat 3000 carburant"\n📊 "résumé"\n💡 "conseil"\n\nC'est parti ! 🚀`,
  }]);
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);
  const biz = getBizType(config.businessType);
  const empId = employee?.id || "owner";

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, typing]);

  const myTxns = useMemo(() => transactions.filter((t) => t.employeeId === empId && t.date === today()), [transactions, empId]);
  const tv = myTxns.filter((t) => t.type === "vente").reduce((s, t) => s + t.amount, 0);
  const td = myTxns.filter((t) => t.type === "depense").reduce((s, t) => s + t.amount, 0);

  const process = useCallback((text) => {
    const p = parseMessage(text);
    let r = "", txn = null;

    switch (p.type) {
      case "vente": {
        txn = { id: uid(), employeeId: empId, employeeName: employee?.name || config.ownerName, type: "vente", amount: p.amount, category: p.category, date: today(), time: now(), timestamp: Date.now() };
        const nv = tv + p.amount;
        r = `✅ Vente enregistrée !\n\n${getCatEmoji(p.category)} ${p.category} → ${fm(p.amount)} FCFA\n\n────────────\n💵 Ventes: ${fm(nv)} F\n💸 Dépenses: ${fm(td)} F\n📈 Bénéfice: ${fm(nv - td)} F\n────────────\n\n${nv - td > 0 ? "Continue comme ça ! 💪" : "Courage ! 🙏"}`;
        playSound("success"); vibrate([50, 30, 50]);
        notifyVente(p.amount, p.category);
        showToast(`+${fm(p.amount)} F — ${p.category}`, "success");
        break;
      }
      case "depense": {
        txn = { id: uid(), employeeId: empId, employeeName: employee?.name || config.ownerName, type: "depense", amount: p.amount, category: p.category, date: today(), time: now(), timestamp: Date.now() };
        const nd = td + p.amount;
        r = `✅ Dépense notée !\n\n${getCatEmoji(p.category)} ${p.category} → ${fm(p.amount)} FCFA\n\n────────────\n💵 Ventes: ${fm(tv)} F\n💸 Dépenses: ${fm(nd)} F\n📈 Bénéfice: ${fm(tv - nd)} F\n────────────`;
        playSound("notification"); vibrate([30]);
        notifyDepense(p.amount, p.category);
        showToast(`-${fm(p.amount)} F — ${p.category}`, "warning");
        break;
      }
      case "resume": {
        if (myTxns.length === 0) { r = "📭 Pas encore de transaction !\n\nEnvoyez \"vente 5000 riz\" pour commencer."; break; }
        r = `📊 BILAN DU JOUR\n${dayName(today())}\n\n────────────\n💵 Ventes : ${fm(tv)} F\n💸 Dépenses : ${fm(td)} F\n📈 Bénéfice : ${fm(tv - td)} F\n────────────\n\n📝 ${myTxns.length} transaction${myTxns.length > 1 ? "s" : ""}`;
        const vDetail = myTxns.filter((t) => t.type === "vente");
        const dDetail = myTxns.filter((t) => t.type === "depense");
        if (vDetail.length > 0) { r += "\n\n🛒 Ventes:"; vDetail.forEach((t) => { r += `\n  ${getCatEmoji(t.category)} ${t.category} → ${fm(t.amount)} F`; }); }
        if (dDetail.length > 0) { r += "\n\n🧾 Dépenses:"; dDetail.forEach((t) => { r += `\n  ${getCatEmoji(t.category)} ${t.category} → ${fm(t.amount)} F`; }); }
        r += `\n\n💡 ${getRandomTip()}`;
        break;
      }
      case "conseil": r = `💡 CONSEIL\n\n${getRandomTip()}\n\n────────────\nTapez "conseil" pour un autre !`; break;
      case "dette": r = "📒 Le cahier de dettes arrive bientôt ! 🔜\n\nEn attendant, notez les dettes dans un cahier papier."; break;
      case "historique": r = "📅 Pour l'historique complet, le patron peut ouvrir le Dashboard 📊"; break;
      case "salut": r = `Salut ${employee?.name || config.ownerName} ! 😄\n\nPrêt à bosser ? 💪\n💵 "vente 5000 riz"\n💸 "achat 3000 carburant"`; break;
      case "merci": r = `De rien ! 😊 Continuez comme ça ! 💪`; break;
      case "aide": r = `📖 MODE D'EMPLOI\n\n💵 Vente: "vente 5000 riz"\n💸 Dépense: "achat 3000 carburant"\n📊 Bilan: "résumé"\n💡 Astuce: "conseil"\n\nAstuce: écrivez naturellement !\n"j'ai vendu poulet 7000" marche aussi 😉`; break;
      case "unclear":
        if (p.hint) r = `🤔 C'est une ${p.hint === "vente" ? "vente" : "dépense"} ? Ajoutez le montant !\nEx: "${p.hint === "vente" ? "vente" : "achat"} 5000 riz"`;
        else if (p.amount) r = `🤔 ${fm(p.amount)} F — vente ou dépense ?\n\n• "vente ${p.amount}"\n• "achat ${p.amount}"`;
        else r = "🤔 Pas compris. Essayez \"vente 5000 riz\"";
        break;
      default: r = "🤔 Pas compris !\n\n💵 \"vente 5000 riz\"\n💸 \"achat 3000 carburant\"\n📊 \"résumé\"";
    }
    return { r, txn };
  }, [config, employee, empId, tv, td, myTxns]);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setMsgs((m) => [...m, { from: "user", text, time: now() }]);
    setInput(""); setTyping(true);
    setTimeout(() => {
      const { r, txn } = process(text);
      if (txn) onTransaction(txn);
      setMsgs((m) => [...m, { from: "bot", text: r, time: now() }]);
      setTyping(false);
    }, 300 + Math.random() * 400);
  }, [input, process, onTransaction]);

  const quickSend = (msg) => {
    setMsgs((m) => [...m, { from: "user", text: msg, time: now() }]);
    setTyping(true);
    setTimeout(() => {
      const { r, txn } = process(msg);
      if (txn) onTransaction(txn);
      setMsgs((m) => [...m, { from: "bot", text: r, time: now() }]);
      setTyping(false);
    }, 350);
  };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: THEME.chatBg }}>
      {/* Header */}
      <div style={{ background: THEME.whatsapp, color: "#fff", padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", padding: "4px 6px" }}>←</button>
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: `linear-gradient(135deg, ${biz.color}, ${biz.color}bb)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{biz.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{config.businessName}</div>
          <div style={{ fontSize: 11, opacity: 0.8 }}>{employee ? `${employee.name} • ${employee.role}` : config.ownerName}</div>
        </div>
      </div>

      {/* Quick stats */}
      {(tv > 0 || td > 0) && (
        <div style={{ background: "#D4ECD0", padding: "5px 12px", display: "flex", justifyContent: "space-around", flexShrink: 0, borderBottom: "1px solid #C5DFC1" }}>
          {[{ l: "Ventes", v: tv, c: THEME.green }, { l: "Dépenses", v: td, c: THEME.red }, { l: "Bénéfice", v: tv - td, c: tv - td >= 0 ? THEME.green : THEME.red }].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "#666", fontWeight: 700, textTransform: "uppercase" }}>{s.l}</div>
              <div style={{ fontSize: 12, fontWeight: 900, color: s.c }}>{fm(s.v)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 8px 6px" }}>
        {msgs.map((m, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: m.from === "user" ? "flex-end" : "flex-start",
            marginBottom: 6, padding: m.from === "user" ? "0 0 0 44px" : "0 44px 0 0",
            animation: "fadeUp 0.2s ease both",
          }}>
            <div style={{
              background: m.from === "user" ? THEME.chatUser : THEME.chatBot,
              borderRadius: m.from === "user" ? "14px 2px 14px 14px" : "2px 14px 14px 14px",
              padding: "8px 12px", maxWidth: 310,
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}>
              <div style={{ fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-line" }}>{m.text}</div>
              <div style={{ fontSize: 10, color: m.from === "user" ? "#7a9e5c" : "#aaa", textAlign: "right", marginTop: 2 }}>
                {m.time}{m.from === "user" && " ✓✓"}
              </div>
            </div>
          </div>
        ))}
        {typing && (
          <div style={{ paddingRight: 44, marginBottom: 6 }}>
            <div style={{ background: "#fff", display: "inline-flex", gap: 4, padding: "10px 16px", borderRadius: "2px 14px 14px 14px" }}>
              {[0,1,2].map((i) => (<div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: "#90A4AE", animation: `bounce 1.2s ease ${i * 0.2}s infinite` }} />))}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick actions */}
      <div style={{ display: "flex", gap: 5, padding: "4px 8px", background: "#f0ebe5", flexShrink: 0, overflowX: "auto" }}>
        {[
          { l: "📊 Résumé", m: "résumé" },
          { l: "💡 Conseil", m: "conseil" },
          { l: "💵 Vente", m: "vente ", fill: true },
          { l: "💸 Achat", m: "achat ", fill: true },
        ].map((q, i) => (
          <button key={i} onClick={() => q.fill ? setInput(q.m) : quickSend(q.m)} style={{
            background: "#fff", border: "1px solid #d4cfc8", borderRadius: 18,
            padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
            color: "#444", whiteSpace: "nowrap",
          }}>{q.l}</button>
        ))}
      </div>

      {/* Input */}
      <div style={{ padding: "6px 8px 8px", background: "#f0ebe5", display: "flex", gap: 8, flexShrink: 0 }}>
        <div style={{ flex: 1, background: "#fff", borderRadius: 22, display: "flex", alignItems: "center", padding: "0 14px" }}>
          <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
            placeholder="Ex: vente 5000 riz..."
            style={{ flex: 1, border: "none", outline: "none", fontSize: 14, background: "transparent", padding: "10px 0", fontFamily: THEME.font }}
          />
        </div>
        <button onClick={send} disabled={!input.trim()} style={{
          width: 42, height: 42, borderRadius: "50%",
          background: input.trim() ? THEME.whatsappLight : "#a0a0a0",
          border: "none", color: "#fff", fontSize: 18,
          cursor: input.trim() ? "pointer" : "default",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: input.trim() ? `0 2px 10px ${THEME.whatsappLight}44` : "none",
          transition: "all 0.2s ease", flexShrink: 0,
        }}>➤</button>
      </div>
    </div>
  );
}
